import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Images from "../utils/Images";

const EASE = [0.22, 1, 0.36, 1];

const VALUES = [
  {
    word: "Simple.",
    image: Images.chooseSimple,
    imageLabel: "01  ·  Simple",
    alt: "A young African professional completing a financial task on a phone in a few clear steps",
    position: "object-top",
  },
  {
    word: "Transparent.",
    image: Images.chooseTransparent,
    imageLabel: "02  ·  Transparent",
    alt: "Side-by-side comparison of financial products with clear fees and terms on a laptop",
    position: "object-center",
  },
  {
    word: "Trusted.",
    image: Images.chooseTrusted,
    imageLabel: "03  ·  Trusted",
    alt: "A banker and a customer reviewing a verified identity check together",
    position: "object-center",
  },
  {
    word: "Connected.",
    image: Images.chooseConnected,
    imageLabel: "04  ·  Connected",
    alt: "People and financial teams collaborating across a shared digital workspace",
    position: "object-center",
  },
  {
    word: "Intelligent.",
    image: Images.chooseIntelligent,
    imageLabel: "05  ·  Intelligent",
    alt: "An advisor using an AI recommendation screen to guide a financial decision",
    position: "object-[center_20%]",
  },
];

const CAPABILITIES = [
  "Artificial Intelligence",
  "Financial Education",
  "Marketplace Technology",
  "Digital Verification",
  "Contact Centre Services",
  "Debt Recovery Automation",
  "Real-Time Analytics",
  "Secure APIs",
];

function Choose() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="choose"
      className="relative overflow-hidden bg-[#f3f7f5] text-navy"
      aria-labelledby="choose-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(229,184,11,0.12),transparent_55%),radial-gradient(55%_40%_at_0%_20%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.36]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Why choose SimFinance
          </p>
          <h2
            id="choose-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Because financial services
            <span className="mt-1 block">should be</span>
          </h2>
        </motion.header>

        <motion.ul
          className="mt-14 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: reduce ? 0 : 0.08 } },
          }}
        >
          {VALUES.map((value) => (
            <motion.li key={value.word} variants={fade}>
              <figure className="relative aspect-[4/5] overflow-hidden bg-navy">
                <MediaFrame
                  src={value.image}
                  alt={value.alt}
                  label={value.imageLabel}
                  position={value.position}
                  className="absolute inset-0 h-full w-full"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/15 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                  <span className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
                    {value.word}
                  </span>
                </figcaption>
              </figure>
            </motion.li>
          ))}
        </motion.ul>

        <div className="mt-20 grid items-center gap-12 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-16">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
              Our ecosystem combines
            </p>
            <ul className="mt-6">
              {CAPABILITIES.map((item, i) => (
                <li
                  key={item}
                  className="flex items-center gap-3 border-t border-navy/10 py-3.5 first:border-t-0 first:pt-0"
                >
                  <span className="font-mono text-[11px] font-semibold tracking-[0.16em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] font-medium text-navy sm:text-base">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 max-w-md text-base leading-relaxed text-navy/60 sm:text-lg">
              …into one integrated financial platform{" "}
              <span className="font-semibold text-navy">built for Africa</span>.
            </p>
          </motion.div>

          <motion.div
            className="relative"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <span className="pointer-events-none absolute -left-2 -top-2 h-10 w-10 border-l border-t border-gold" />
            <span className="pointer-events-none absolute -right-2 -top-2 h-10 w-10 border-r border-t border-navy/20" />
            <span className="pointer-events-none absolute -bottom-2 -left-2 h-10 w-10 border-b border-l border-navy/20" />
            <span className="pointer-events-none absolute -bottom-2 -right-2 h-10 w-10 border-b border-r border-gold" />

            <div className="relative min-h-[22rem] overflow-hidden bg-navy lg:min-h-[32rem]">
              <MediaFrame
                src={Images.choosePlatform}
                alt="An integrated SimFinance platform connecting education, marketplace, verification and recovery across Africa"
                label="06  ·  Platform"
                position="object-center"
                className="absolute inset-0 h-full w-full"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/55 via-transparent to-navy/20" />
              <div className="absolute left-5 top-5 flex items-center gap-3 sm:left-6 sm:top-6">
                <span className="font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
                  06 / 06
                </span>
                <span className="h-px w-8 bg-white/30" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/70">
                  One platform
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MediaFrame({ src, alt, label, className, position = "object-center" }) {
  const [failed, setFailed] = useState(false);
  const showFallback = failed || !src;

  if (showFallback) {
    return (
      <div className={`relative overflow-hidden bg-navy ${className}`} role="img" aria-label={alt}>
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -right-10 top-8 h-40 w-40 rounded-full bg-royal/35 blur-3xl" />
        <div className="absolute -bottom-12 left-6 h-32 w-32 rounded-full bg-gold/20 blur-3xl" />
        <div className="relative flex h-full min-h-[inherit] flex-col justify-between p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold/90">
            {label}
          </p>
          <p className="max-w-[14rem] text-sm leading-relaxed text-white/55">
            Placeholder — generate and drop the image into this slot.
          </p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover ${position} ${className}`}
      onError={() => setFailed(true)}
    />
  );
}

export default Choose;
